import React, { useEffect, useState, useCallback } from 'react'
import { Table, Tag, Space, Button, Select, message, Typography, Badge, Descriptions, Popconfirm } from 'antd'
import { SendOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { warehouseApi } from '../../services/api'
import dayjs from 'dayjs'

const { Title } = Typography

const STATUS_MAP = {
  '待发货': { color: 'orange' },
  '已发货': { color: 'blue' },
  '已签收': { color: 'green' },
}

export default function StockOut() {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState({ page: 1, pageSize: 20, status: '' })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await warehouseApi.stockOuts(query)
      setData(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleShip = async (id) => {
    try {
      await warehouseApi.shipStockOut(id)
      message.success('已确认发货')
      fetchData()
    } catch (e) {
      message.error(e?.error || '操作失败')
    }
  }

  const handleReceive = async (id) => {
    try {
      await warehouseApi.receiveStockOut(id)
      message.success('已确认签收，库存已扣减')
      fetchData()
    } catch (e) {
      message.error(e?.error || '操作失败')
    }
  }

  const getItemCount = (record) => {
    return record.items ? record.items.length : 0
  }

  const expandedRowRender = (record) => {
    const columns = [
      { title: '产品名称', key: 'name', render: (_, item) => item.product?.name || '—' },
      { title: 'SKU', key: 'sku', render: (_, item) => item.product?.sku || '—' },
      { title: '数量', dataIndex: 'quantity', key: 'quantity' },
      { title: '单位', key: 'unit', render: (_, item) => item.product?.unit || '台' },
    ]
    return (
      <Table
        columns={columns}
        dataSource={record.items}
        rowKey="id"
        pagination={false}
        size="small"
      />
    )
  }

  const columns = [
    {
      title: '出库单号', dataIndex: 'code', key: 'code', width: 140,
    },
    {
      title: '客户', dataIndex: 'customerName', key: 'customerName', width: 140,
      render: (v) => v || '—',
    },
    {
      title: '产品明细', key: 'items', width: 100,
      render: (_, r) => <Badge count={getItemCount(r)} overflowCount={99} showZero style={{ backgroundColor: '#1677ff' }}><span style={{ paddingRight: 8 }}>项</span></Badge>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (v) => <Tag color={STATUS_MAP[v]?.color || 'default'}>{v}</Tag>,
    },
    {
      title: '日期', dataIndex: 'createdAt', key: 'createdAt', width: 120,
      render: (v) => dayjs(v).format('MM-DD HH:mm'),
    },
    {
      title: '操作人', key: 'operator', width: 80,
      render: (_, r) => r.operator?.name || '—',
    },
    {
      title: '操作', key: 'actions', width: 200,
      render: (_, r) => (
        <Space>
          {r.status === '待发货' && (
            <Popconfirm title="确认发货？" onConfirm={() => handleShip(r.id)} okText="确认" cancelText="取消">
              <Button size="small" icon={<SendOutlined />} type="primary">确认发货</Button>
            </Popconfirm>
          )}
          {r.status === '已发货' && (
            <Popconfirm title="确认签收？将扣减库存" onConfirm={() => handleReceive(r.id)} okText="确认签收" cancelText="取消">
              <Button size="small" icon={<CheckCircleOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }}>确认签收</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>出库管理</Title>
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="筛选状态"
          allowClear
          style={{ width: 140 }}
          onChange={(v) => setQuery((q) => ({ ...q, status: v || '', page: 1 }))}
        >
          <Select.Option value="待发货">待发货</Select.Option>
          <Select.Option value="已发货">已发货</Select.Option>
          <Select.Option value="已签收">已签收</Select.Option>
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowRender,
          rowExpandable: (r) => getItemCount(r) > 0,
        }}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 条出库记录`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />
    </div>
  )
}
